import { FormattedPercentage, PopoverOpenButton } from "@msys/ui";
import { Help as HelpIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { Form, Formik } from "formik";
import React from "react";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import { WorkRatesField } from "../../doc-items/fields/WorkRatesField.js";
import type { Vertical } from "../../verticals/types.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { ModifyItemCalculationFn } from "../quote/useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function WorkMarginCell({
  itemId,
  vertical,
  workBuyingRate,
  workSellingRate,
  canEdit,
  modifyItemCalculation,
}: {
  itemId: string;
  vertical: Vertical | null;
  workBuyingRate: number;
  workSellingRate: number;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
}) {
  const workMargin =
    workBuyingRate !== 0
      ? workSellingRate / workBuyingRate - 1
      : workSellingRate === 0
        ? 0
        : workSellingRate > 0
          ? 1
          : -1;

  const initialValues = React.useMemo(
    () => ({ workBuyingRate, workSellingRate }),
    [workBuyingRate, workSellingRate]
  );

  const popoverContent = canEdit ? (
    <Formik
      initialValues={initialValues}
      onSubmit={async values => {
        await modifyItemCalculation(itemId, {
          workBuyingRate: values.workBuyingRate,
          workSellingRate: values.workSellingRate,
        });
      }}
    >
      <Form>
        <WorkRatesField
          vertical={vertical}
          isEditable={true}
          isWorkRateEditable={true}
          workBuyingRate={workBuyingRate}
          workSellingRate={workSellingRate}
        />
        <AutoSave
          type={null}
          initialValues={initialValues}
          enableReinitialize
        />
      </Form>
    </Formik>
  ) : (
    <WorkRatesField
      vertical={vertical}
      isEditable={false}
      isWorkRateEditable={false}
      workBuyingRate={workBuyingRate}
      workSellingRate={workSellingRate}
    />
  );

  return (
    <Stack direction="row" alignItems="center">
      <FormattedPercentage value={workMargin} />
      <PopoverOpenButton content={popoverContent}>
        <IconButton size="small" color="secondary">
          <HelpIcon fontSize="small" />
        </IconButton>
      </PopoverOpenButton>
    </Stack>
  );
}

export function createWorkMarginColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "workMargin",
    headerName: options.headerName,
    width: 80,
    editable: false,
    sortable: false,
    filterable: false,
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      const calc = params.row.item.proposedCalculation;
      if (!calc) return null;
      return (
        <WorkMarginCell
          itemId={params.row.item.id}
          vertical={
            (params.row.item.vertical ??
              params.row.item.inheritedVertical ??
              null) as Vertical | null
          }
          workBuyingRate={calc.workBuyingRate}
          workSellingRate={calc.workSellingRate}
          canEdit={options.canEdit}
          modifyItemCalculation={options.modifyItemCalculation}
        />
      );
    },
  };
}
